/**
 * Stage edit scope — which stages to clear and re-walk when editing one stage.
 *
 * Belongs here: STAGE_DEPENDS_ON and helpers that derive the edit stage set.
 * Does not belong here: question visibility (conditionalExpression) or navigation.
 *
 * Prefer API `dependsOnStageIds` later — replace this map, keep the helpers.
 */
import {
  StrategyQuestionnaireStageId,
  type StrategyQuestionnaireStage
} from '../shared/types/questionnaire.types'

/**
 * Child stage → stages it depends on.
 * Example: sip depends on life_stage → editing life_stage also clears + re-walks sip.
 */
export const STAGE_DEPENDS_ON: Partial<
  Record<StrategyQuestionnaireStageId, StrategyQuestionnaireStageId[]>
> = {
  [StrategyQuestionnaireStageId.SipAllocation]: [StrategyQuestionnaireStageId.LifeStage]
}

/**
 * Stages that directly list `parentStageId` in STAGE_DEPENDS_ON.
 */
function getDirectDependentStageIds(parentStageId: string): string[] {
  const dependentIds: string[] = []
  for (const stageId of Object.values(StrategyQuestionnaireStageId)) {
    const dependsOn = STAGE_DEPENDS_ON[stageId] ?? []
    if (dependsOn.some((dependencyId) => dependencyId === parentStageId)) {
      dependentIds.push(stageId)
    }
  }
  return dependentIds
}

/**
 * Edited stage + every stage that depends on it (transitively).
 * Order is discovery order — use getEditStagesInApiOrder for walk/clear order.
 */
export function getEditStageIds(editedStageId: string): string[] {
  const editStageIds = [editedStageId]
  const queue = [editedStageId]
  const seen = new Set<string>([editedStageId])

  while (queue.length > 0) {
    const parentStageId = queue.shift()
    if (parentStageId == null) break
    for (const dependentId of getDirectDependentStageIds(parentStageId)) {
      if (seen.has(dependentId)) continue
      seen.add(dependentId)
      editStageIds.push(dependentId)
      queue.push(dependentId)
    }
  }

  return editStageIds
}

/**
 * Stages to clear and re-walk for an edit, in questionnaire API order.
 */
export function getEditStagesInApiOrder(
  editedStageId: string,
  stages: StrategyQuestionnaireStage[]
): StrategyQuestionnaireStage[] {
  const editStageIdSet = new Set(getEditStageIds(editedStageId))
  return (stages ?? []).filter((stage) => stage?.id != null && editStageIdSet.has(stage.id))
}
